import React, { createContext, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PHONE_ACTION_BAR_PINNED } from '../../constants/overlayLayers';
import { useIsMobileOrderViewport } from '../../hooks/useIsMobileOrderViewport';

/**
 * THE phone (below `md`) home of an order's DECISION buttons — Approve / Reject
 * (owner 2026-10-04: *"the approve and reject buttons are right under 'actions'
 * and easy to press by accident … Pinned is good but what if they make an edit
 * so we also have save and discard?"*).
 *
 * ONE pinned bar, ONE pair at a time, never stacked:
 *   untouched order  → this bar: [Reject] [Approve], pinned above the nav;
 *   unsaved changes  → this bar steps aside, and `OrderForm`'s own pinned
 *                      Save / Discard bar shows "Save your changes to approve";
 *   saved/discarded  → this bar is back.
 * So nobody can approve a version that is not the saved one from a phone.
 *
 * 🔑 Desktop is UNTOUCHED: from `md` up the wrapper is `display: contents`, so
 * the buttons sit in the caller's header group exactly as before (and keep the
 * desktop save-then-act prompt). It wraps the caller's OWN buttons — same
 * handlers, same testids, never a copy.
 *
 * `above` — an optional phone-only node shown at the top of the bar (To
 * Approve's reject reason box, which must stay beside its Confirm button).
 *
 * Reads `OrderFormDecisionContext`, provided by `OrderForm`: `dirty` (the same
 * condition that shows its Save bar) and `setHasDecision` (so the Save bar
 * knows to show the hint). Outside an `OrderForm` (a read-only panel) the
 * default is clean, so the bar simply pins.
 */
export const OrderFormDecisionContext = createContext({ dirty: false, setHasDecision: null });

export const PHONE_DECISION_HINT = 'Save your changes to approve';

export default function PhoneDecisionBar({ children, above = null, testId }) {
  const { dirty, setHasDecision } = useContext(OrderFormDecisionContext);
  useEffect(() => {
    if (!setHasDecision) return undefined;
    setHasDecision(true);
    return () => setHasDecision(false);
  }, [setHasDecision]);

  const isPhone = useIsMobileOrderViewport();

  // Desktop: the caller's buttons, in place, exactly as before.
  if (!isPhone) return <>{children}</>;
  // Phone, unsaved changes: stepped aside (the Save bar says so).
  if (dirty) return null;

  // 🪤 PORTALLED to <body>: the phone order tables mask their rows for the
  // sideways edge-fade (`ui/edgeFade`), and a mask clips a `fixed` descendant —
  // measured on To Approve, where the bar painted but a tap landed on <main>.
  // A portal keeps React context and event bubbling (the buttons stopPropagation).
  // The SAME pinned-bar classes `PhoneActionBar` uses, but NO spacer: the list
  // pages already leave room below the last row (pb-36).
  return createPortal(
    <div
      className={`flex flex-wrap justify-center gap-3 [&>button]:flex-1 ${PHONE_ACTION_BAR_PINNED}`}
      data-testid={testId}
      data-phone-pinned="true"
    >
      {above ? <div className="w-full">{above}</div> : null}
      {children}
    </div>,
    document.body
  );
}
